import React, { useContext, useEffect, useState } from 'react';
import {
  MinusIcon,
  MoreIcon,
  PlaneIcon,
} from '@womcorp/wom-miwom-ui-kit/dist/@theme/assets/svg/icons';
import { AutoComplete } from '@womcorp/wom-miwom-ui-kit/dist/components/AutoComplete';
import { PinPukHelpIcon as DeviceIcon } from '@womcorp/wom-miwom-ui-kit/dist/@theme/assets/svg/icons/help';
import { navigate } from 'gatsby';
import {
  ErrorPaymentsIcon,
  InternalServerErrorIcon,
} from '@womcorp/wom-miwom-ui-kit/dist/@theme/assets/svg/pictures';
import { GoogleTagManager } from '../../helpers';

import {
  RoamingContainerStyled,
  RoamingContentStyled,
  RoamingHeaderStyled,
  RoamingSelectorStyled,
  IconContainerStyled,
  CountriesContainerStyled,
  FrequentCountriesContainerStyled,
  FrequentCountrieStyled,
  ChevronStyled,
  SeeMoreButtonStyled,
  ErrorContainerStyled,
} from './styles/RoamingSearchStyled';
import { useGlobalData } from '../../contextProvider/GlobalDataContext';
import RoamingSkeleton from './RoamingSkeleton/RoamingSkeleton';
import { getFrequentCountries } from '../../services/Contentful';
import useUser from '../../hooks/useUser';
import MarketplaceService from '../../services/Marketplace';
import ErrorTemplate from '../Errors/ErrorTemplate';
import PageContext from '../../contextProvider/PageContext';

const Roaming = () => {
  const locationState = useContext(PageContext);

  const {
    location: { state: stateLocation },
  } = locationState;

  const { bundlesLoaded = [] } = stateLocation ?? {};

  const {
    plan: { number },
  } = useUser();
  const [frequentCountries, setFrequentCountries] = useState([]);
  const [roamingBundles, setRoamingBundles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const [showHiddenCountries, setShowHiddenCountries] = useState(false);
  const { 'roaming-title': roamingTitle = {} } = useGlobalData();
  const maxSlice = !showHiddenCountries ? 5 : frequentCountries.length;

  const loadData = () => {
    if (!loading) setLoading(true);

    const bundlesPromise =
      bundlesLoaded.length > 0
        ? Promise.resolve(bundlesLoaded)
        : MarketplaceService.getBundlesPerCategory(5, 'ROAMING', 5, number);

    return Promise.allSettled([getFrequentCountries(), bundlesPromise])
      .then(([frequentCountriesResult, bundlesResult]) => {
        if (frequentCountriesResult.status === 'fulfilled') {
          const [
            { items: frequentCountriesRoaming },
          ] = frequentCountriesResult.value;
          setFrequentCountries(frequentCountriesRoaming);
        } else {
          setFrequentCountries([]);
        }

        if (bundlesResult.status === 'rejected') {
          throw bundlesResult.reason;
        }

        setRoamingBundles(bundlesResult.value);
        const [{ category: roamingBundlesCategory }] = bundlesResult.value;

        const allCountries = roamingBundlesCategory.subcategories.flatMap(
          sub => sub.countries || []
        );

        const uniqueCountries = [...new Set(allCountries)]
          .map(country => ({ value: country, label: country }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setCountries(uniqueCountries);

        setRetryCount(0);
      })
      .catch(() => {
        setRetryCount(prevRetryCount => prevRetryCount + 1);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    GoogleTagManager.pushToDataLayer({
      event: 'pagina-virtual',
      page: '/tienda/bolsas/roaming/buscador',
      pagename: 'Tienda - Bolsas - Roaming - Buscador',
    });
    loadData();
  }, []);

  if (loading) return <RoamingSkeleton />;

  if (!loading && retryCount) {
    const errorComponent =
      retryCount > 3 ? (
        <ErrorTemplate
          title="Error Interno del Servidor"
          icon={<InternalServerErrorIcon />}
          onClickButton={() => {
            navigate('/home');
          }}
          buttonText="Ir al inicio"
          markOptions={{
            event: 'pagina-virtual',
            page: `/tienda/bolsas/roaming/buscador/error/servidor`,
            pagename: 'Tienda - Bolsas - Roaming - Buscador - Error Servidor',
          }}
        >
          <span>Nuestro sistema ha fallado, vuelve más tarde</span>
        </ErrorTemplate>
      ) : (
        <ErrorTemplate
          title="Ha ocurrido un error"
          icon={<ErrorPaymentsIcon />}
          onClickButton={() => {
            loadData();
          }}
          buttonText="Reintentar"
          markOptions={{
            event: 'pagina-virtual',
            page: `/tienda/bolsas/roaming/buscador/error`,
            pagename: 'Tienda - Bolsas - Roaming - Buscador - Error',
          }}
          onBackLinkClick={() => {
            navigate('/home');
          }}
          backText="Cancelar"
        >
          <span>
            No se ha podido completar tu solicitud, vuelve a intentarlo
          </span>
        </ErrorTemplate>
      );

    return <ErrorContainerStyled>{errorComponent}</ErrorContainerStyled>;
  }

  return (
    <>
      <RoamingHeaderStyled>
        <IconContainerStyled>
          <DeviceIcon />
        </IconContainerStyled>
        <p>{roamingTitle.title}</p>
      </RoamingHeaderStyled>
      <RoamingContainerStyled>
        <RoamingContentStyled>
          <PlaneIcon />
          <h1>¿En qué país utilizarás tu roaming?</h1>
        </RoamingContentStyled>
        <RoamingSelectorStyled>
          <h2>Selecciona el país</h2>
          <AutoComplete
            options={countries}
            label="¿Dónde viajarás?"
            notFound="No se encontró ningún país"
            onChange={e => {
              if (e?.value) {
                const isFrequentcountry = frequentCountries.find(
                  country => country.value === e.value
                );
                const flag = isFrequentcountry?.flagIcon?.url ?? '';
                navigate(
                  `/marketplace/categorias/bolsas-roaming?code=5&country=${e.value}`,
                  {
                    state: {
                      icon: flag,
                      countryName: e.label,
                      bundlesLoaded: roamingBundles,
                    },
                  }
                );
              }
            }}
          />
        </RoamingSelectorStyled>
        {frequentCountries.length >= 1 && (
          <FrequentCountriesContainerStyled>
            <h1>Destinos recurrentes</h1>
            <CountriesContainerStyled>
              {frequentCountries
                .slice(0, maxSlice)
                .map(({ title, flagIcon, value }) => (
                  <FrequentCountrieStyled
                    key={title}
                    onClick={() => {
                      navigate(
                        `/marketplace/categorias/bolsas-roaming?code=5&country=${value}`,
                        {
                          state: {
                            icon: flagIcon?.url,
                            countryName: title,
                            bundlesLoaded: roamingBundles,
                          },
                        }
                      );
                    }}
                  >
                    <img src={flagIcon?.url} alt={flagIcon?.description} />
                    <p>{title}</p>
                    <ChevronStyled />
                  </FrequentCountrieStyled>
                ))}
            </CountriesContainerStyled>

            {frequentCountries.length > 5 && (
              <SeeMoreButtonStyled
                onClick={() => setShowHiddenCountries(!showHiddenCountries)}
              >
                {!showHiddenCountries ? (
                  <>
                    VER MÁS <MoreIcon />
                  </>
                ) : (
                  <>
                    OCULTAR <MinusIcon />
                  </>
                )}
              </SeeMoreButtonStyled>
            )}
          </FrequentCountriesContainerStyled>
        )}
      </RoamingContainerStyled>
    </>
  );
};

export default Roaming;
